import React from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { closeModal } from '../../../store/slices/uiSlice';
import Modal from '../Modal';
import { CareRespiteDateModal, EditPaymentMethodModal, EditFieldModal } from '../../modals';
import { SectionKey } from '../../../constants/quizConstants';
import { useAuth } from '../../../hooks/useAuth';
import styles from './GlobalModal.module.scss';

const LogoutConfirmFooter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { logout } = useAuth();

  const handleCancel = () => {
    dispatch(closeModal());
  };

  const handleConfirm = () => {
    dispatch(closeModal());
    logout();
  };

  return (
    <div className={styles.logoutFooter}>
      <button type="button" className={styles.cancelButton} onClick={handleCancel}>
        Abbrechen
      </button>
      <button type="button" className={styles.confirmButton} onClick={handleConfirm}>
        Abmelden
      </button>
    </div>
  );
};

const GlobalModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const modal = useAppSelector((state) => state.ui.modal);

  const handleClose = () => {
    dispatch(closeModal());
  };

  if (!modal.isOpen || modal.modalType === 'none') return null;

  // EditPaymentMethodModal renders its own overlay
  if (modal.modalType === 'editPaymentMethod') {
    return <EditPaymentMethodModal />;
  }

  // EditFieldModal renders its own overlay
  if (modal.modalType === 'editField' && modal.editFieldData) {
    return (
      <EditFieldModal
        fieldKey={modal.editFieldData.fieldKey}
        fieldLabel={modal.editFieldData.fieldLabel}
        currentValue={modal.editFieldData.currentValue}
        section={modal.editFieldData.section as SectionKey}
        fieldType={modal.editFieldData.fieldType}
      />
    );
  }

  // Logout confirmation: custom modal with footer
  if (modal.modalType === 'logoutConfirm') {
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={handleClose}
        title="Abmelden"
        large={false}
        footer={<LogoutConfirmFooter />}
      >
        <p>Sind Sie sicher, dass Sie sich abmelden möchten?</p>
      </Modal>
    );
  }

  // Render different modal content based on modalType
  const renderModalContent = () => {
    switch (modal.modalType) {
      case 'careRespiteDate':
        return <CareRespiteDateModal />;
      case 'confirmation':
        return (
          <div>
            <p>{modal.bodyText}</p>
          </div>
        );
      case 'alert':
        return (
          <div>
            <p>{modal.bodyText}</p>
          </div>
        );
      default:
        return null;
    }
  };

  // Determine if we should show close button based on modal type
  const hideCloseButton = modal.modalType === 'careRespiteDate';
  const preventOutsideClose = modal.modalType === 'careRespiteDate';

  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={preventOutsideClose ? undefined : handleClose}
      title={modal.headerText}
      large={modal.large}
      hideCloseButton={hideCloseButton}
    >
      {renderModalContent()}
    </Modal>
  );
};

export default GlobalModal;
